import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	InfinityList,
	InfinityListItemWithId,
	InfinityListView,
} from 'components/InfinityList';
import {useNavigate, useParams} from 'react-router-dom';
import {SelectionMode} from 'components/FluentUICompat';
import {useInfinityListStandardHooks} from 'components/InfinityList/InfinitListStandardHooks';
import {GetVehicleProjectDetailsQuery} from '../hooks';
import {
	useGetVehicleProjectMasterRegDocQueryLazyQuery,
	useGetVehicleProjectRegDocNextLazyQuery,
	useGetVehicleProjectRegDocSortOrderLazyQuery,
} from '../hooks/AssignedRegDocsInfinity.generated';
import {ProjectDetailsPageParams} from '../vehicleProjectPagePaths';
import {
	useVehicleProjectRegDocTabColumns,
	useVehicleProjectRegDocTabColumnMetaData,
	useVehicleProjectRegDocTabFilterOptions,
} from './AssignedRegDocColumns';

const initialSortField = 'regulationNumber';
const initialSortOrder = 'ASC';
const intBatchSize = 50;

export type VehicleProjectDetailsTabRegDocsProps = {
	// eslint-disable-next-line react/no-unused-prop-types
	dataVehPro: GetVehicleProjectDetailsQuery | undefined;
	hidden: boolean;
};

const VehicleProjectDetailsTabRegDocs: React.FC<
	VehicleProjectDetailsTabRegDocsProps
> = ({hidden}) => {
	const {t: tTitle} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'RegulatoryDocumentsList',
	});

	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;

	const [getSortOrder] = useGetVehicleProjectRegDocSortOrderLazyQuery();

	const [getRegDocQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetVehicleProjectMasterRegDocQueryLazyQuery();

	const [getRegDocNext, {loading: loadingDocuments}] =
		useGetVehicleProjectRegDocNextLazyQuery();

	const navigate = useNavigate();

	const views: InfinityListView[] = useMemo(() => [], []);

	const initialView = 'STD';

	const arrIds = useMemo(
		() => dataQuery?.vehicleProjectMasterRegDocQuery.regDocIds ?? [],
		[dataQuery],
	);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		const {data} = await getRegDocQuery({
			variables: {
				queryArgAssignedRegDocs: {
					modelSeries,
					generation,
					queryArgs: {query, sortField, sortOrder},
				},
			},
		});
		const arrDocumentNext =
			data?.vehicleProjectMasterRegDocQuery.regDocsBatch ?? [];
		const arrIDsNew = data?.vehicleProjectMasterRegDocQuery.regDocIds ?? [];

		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data} = await getRegDocNext({
			variables: {nextArgs: {preFilteredIds}},
		});

		return data?.voProRegDocNext ?? [];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data} = await getSortOrder({
			variables: {sortArgs: {sortField, preFilteredIds}},
		});

		return data?.voProRegDocSortOrder ?? [];
	};

	const requirementCountMap = useMemo(() => {
		const pairs =
			dataQuery?.vehicleProjectMasterRegDocQuery.requirementCountPerRegDoc ??
			[];
		return Object.fromEntries(pairs.map(p => [p.key, p.value]));
	}, [dataQuery]);

	const projectRelevancyMap = useMemo(() => {
		const pairs =
			dataQuery?.vehicleProjectMasterRegDocQuery.projectRelevancyPerRegDoc ??
			[];
		return Object.fromEntries(pairs.map(p => [p.key, p.value]));
	}, [dataQuery]);

	const columns = useVehicleProjectRegDocTabColumns(
		requirementCountMap,
		projectRelevancyMap,
	);
	const columnMetaData = useVehicleProjectRegDocTabColumnMetaData();
	const columnFilterOptions = useVehicleProjectRegDocTabFilterOptions(
		dataQuery?.vehicleProjectMasterRegDocQuery.regDocFilterOptions,
	);

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	});

	const handleViewingItem = (item: InfinityListItemWithId) => {
		navigate({pathname: `/regulatoryDocuments/${item.id}`});
	};

	const createHref = (item: unknown) =>
		`/regulatoryDocuments/${(item as InfinityListItemWithId).id}`;

	const numberOfElements = useMemo(() => arrIds.length, [arrIds]);

	return (
		<>
			{!hidden && (
				<InfinityList
					pageName={tTitle('RegulatoryDocuments')}
					items={arrDocument}
					sticky={true}
					columns={columns}
					columnMetaData={columnMetaData}
					columnFilterOptions={columnFilterOptions}
					selectionMode={SelectionMode.multiple}
					stillLoading={loadingQuery || loadingDocuments}
					initalFilters={linksFilters}
					initialSortField={initialSortField}
					initialSortOrder={initialSortOrder}
					initialView={initialView}
					keyPrefix={keyPrefix}
					firstRender={firstRenderSTD}
					loadItemsNext={loadItemsNextSTD}
					applyFilter={applyFilterSTD}
					applySort={applySortSTD}
					handleInvokeItem={handleViewingItem}
					createHref={createHref}
					numberOfElements={numberOfElements}
					showHeader={false}
					views={views}
				/>
			)}
		</>
	);
};

export default VehicleProjectDetailsTabRegDocs;
